import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingAccess,
  SettingAgentEditability,
  SettingAvailability,
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type SupportFlatTeamSettingGroupId =
  | "support.general"
  | "support.email_integrations"
  | "support.ticket_routing"
  | "support.ai"
  | "support.ai_channels"
  | "support.billing"
  | "support.voice"
  | "support.contact_form"
  | "support.csat"
  | "support.social"
  | "support.sms"
  | "support.faq"
  | "support.live_chat_widget"
  | "support.concierge_handoff";

const supportSettingsOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.support",
  product: "support",
};

const customerWidgetSettingsOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.customerWidget",
  product: "customer-widget",
};

const lowRisk: SettingRisk = { level: "low" };
const mediumCustomerFacingRisk: SettingRisk = {
  level: "medium",
  reason: "Changes can affect customer-visible support experiences.",
};

const requiresApproval: SettingAgentEditability = { mode: "requiresApproval" };
// Mirrors `useSupportSettings`: Support settings pages are not surfaced until
// Support is enabled. Setup stays on explicit support onboarding journeys.
const requiresSupportEnabledAvailability = {
  conditions: [
    { type: "settingEquals", settingId: "support/enabled", value: true },
  ],
  unavailableBehavior: "hidden",
  reason:
    "Support settings pages are only surfaced after Support is enabled for this merchant.",
} as const satisfies SettingAvailability;
const requiresSupportEnabled = {
  availability: requiresSupportEnabledAvailability,
} as const;

function readonlyRestrictedAccess(reason: string): SettingAccess {
  return {
    read: { minVisibility: "restricted" },
    write: { mode: "none", reason },
  };
}

export const SupportFlatTeamSettingGroups = [
  {
    id: "support.general",
    title: "Support Inbox",
    description:
      "Core support enablement, sender identity, tags, reply defaults, and inbox display behavior.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: lowRisk,
    agentEditability: requiresApproval,
    ...requiresSupportEnabled,
    fullPageTarget: { type: "settings", path: "general" },
    synonyms: [
      "support settings",
      "inbox settings",
      "email support",
      "helpdesk",
      "help desk",
      "helpdesk inbox",
      "omnichannel inbox",
      "ticketing",
      "support tickets",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing global support enablement, sender identity, or reply defaults.",
    },
  },
  {
    id: "support.email_integrations",
    title: "Email Integrations",
    description:
      "Support Email, Gmail, Outlook, custom sender email setup, reply identity, signatures, email exclusions, and spam handling.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    ...requiresSupportEnabled,
    fullPageTarget: { type: "settings", path: "channels/supportEmail" },
    synonyms: [
      "support email",
      "email integrations",
      "email channels",
      "gmail",
      "outlook",
      "custom support email",
      "sender emails",
      "reply email",
      "spam detection",
      "support integrations",
      "helpdesk integrations",
      "connect email",
    ],
    changeGuidance: {
      summary:
        "Confirm before changing connected email identities, reply defaults, or provider-driven spam handling.",
    },
  },
  {
    id: "support.ticket_routing",
    title: "Ticket Routing",
    description:
      "Assignment, reassignment, merge, split, reopen, and archive behavior for support tickets.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    ...requiresSupportEnabled,
    fullPageTarget: { type: "settings", path: "general" },
    synonyms: ["assignment", "routing", "ticket ownership"],
  },
  {
    id: "support.ai",
    title: "Support AI",
    description:
      "Copilot, autopilot, live-chat automation, AI copy, guardrails, and AI tool windows.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_AI_SETTINGS),
    owner: supportSettingsOwner,
    risk: {
      level: "medium",
      reason:
        "Changes can affect automated customer-facing support responses and notifications.",
    },
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "ai/general?tab=general" },
    synonyms: ["autopilot", "copilot", "support automation"],
    changeGuidance: {
      summary:
        "Explain which channels or ticket types AI will affect before enabling automation.",
      askBeforeChanging: [
        "Which support channels should AI handle?",
        "Should AI draft responses only, or resolve eligible tickets automatically?",
      ],
    },
  },
  {
    id: "support.ai_channels",
    title: "AI Channels",
    description:
      "Channel-specific enablement for support AI across email, social, SMS, and messaging providers.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_AI_SETTINGS),
    owner: supportSettingsOwner,
    risk: {
      level: "medium",
      reason:
        "Changes can allow AI to respond on additional customer-facing channels.",
    },
    agentEditability: requiresApproval,
    fullPageTarget: { type: "settings", path: "ai/general?tab=general" },
    synonyms: ["AI platforms", "AI enabled platforms"],
  },
  {
    id: "support.billing",
    title: "Support Billing",
    description:
      "Support, AI, SMS, MMS, and voice billing configuration and usage counters.",
    productArea: "support",
    access: readonlyRestrictedAccess(
      "Support billing settings are owned by billing and subscription workflows.",
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.support.billing",
      product: "billing",
    },
    affectsBilling: true,
    risk: {
      level: "high",
      reason:
        "Billing state and usage counters affect subscriptions and invoices.",
    },
    agentEditability: {
      mode: "never",
      reason:
        "Billing state and usage counters must be changed through billing workflows.",
    },
    ...requiresSupportEnabled,
    fullPageTarget: { type: "settings", path: "billing?currentTab=support" },
    synonyms: ["support pricing", "support usage", "support subscription"],
  },
  {
    id: "support.voice",
    title: "Voice Support",
    description:
      "Voice provider setup, call routing, availability, hold behavior, greetings, and voicemail.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: {
      level: "medium",
      reason:
        "Changes can affect customer phone routing and connected voice provider behavior.",
    },
    agentEditability: requiresApproval,
    ...requiresSupportEnabled,
    fullPageTarget: {
      type: "settings",
      path: "channels/voice-settings?tab=voice",
    },
    synonyms: ["phone support", "voice settings", "calls"],
  },
  {
    id: "support.contact_form",
    title: "Contact Form",
    description:
      "Hosted contact form copy, fields, actions, styling, reply identity, and upload behavior.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    ...requiresSupportEnabled,
    fullPageTarget: { type: "settings", path: "general/contact-form" },
    synonyms: ["contact us form", "support form"],
  },
  {
    id: "support.csat",
    title: "CSAT Surveys",
    description:
      "Customer satisfaction survey enablement, copy, timing, methods, remarks, and header image.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: {
      level: "medium",
      reason:
        "Changes can affect customer-facing survey sends and survey copy.",
    },
    agentEditability: requiresApproval,
    ...requiresSupportEnabled,
    fullPageTarget: { type: "settings", path: "customer-satisfaction" },
    synonyms: ["satisfaction survey", "customer satisfaction"],
  },
  {
    id: "support.social",
    title: "Social Channels",
    description:
      "Facebook and Instagram comment handling, mention behavior, and Meta page data.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: {
      level: "medium",
      reason:
        "Changes can affect public social-channel handling and connected Meta provider behavior.",
    },
    agentEditability: requiresApproval,
    ...requiresSupportEnabled,
    fullPageTarget: { type: "settings", path: "channels/meta-settings" },
    synonyms: ["facebook support", "instagram support", "social comments"],
  },
  {
    id: "support.sms",
    title: "SMS Support",
    description: "SMS support enablement and compliance links.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: {
      level: "medium",
      reason:
        "Changes can affect customer-facing SMS support and compliance links.",
    },
    agentEditability: requiresApproval,
    ...requiresSupportEnabled,
    fullPageTarget: {
      type: "settings",
      path: "channels/voice-settings?tab=sms",
    },
    synonyms: ["text support", "text messaging"],
  },
  {
    id: "support.faq",
    title: "FAQ Styling",
    description:
      "FAQ page branding, colors, imagery, collection display, and brand kit overrides.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    ...requiresSupportEnabled,
    fullPageTarget: { type: "legacySupport", path: "/faq/editor/theme" },
    synonyms: ["help center", "FAQ page", "support knowledge base"],
  },
  {
    id: "support.live_chat_widget",
    title: "Live Chat Widget",
    description:
      "Customer widget support chat copy, availability, fallback conversion, and visible support controls.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: customerWidgetSettingsOwner,
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    ...requiresSupportEnabled,
    fullPageTarget: { type: "settings", path: "customer-widget?tab=support" },
    synonyms: [
      "chat widget",
      "support widget",
      "live chat",
      "enable chat widget",
      "disable chat widget",
      "turn on the chat widget",
      "turn off the chat widget",
    ],
    dependencies: [
      {
        key: "support/ai/automateLiveChat",
        relationship: "related",
        description:
          "AI live-chat automation changes how widget conversations are handled.",
      },
      {
        group: "support.concierge_handoff",
        relationship: "related",
        description:
          "Concierge handoff settings affect widget escalation behavior.",
      },
    ],
  },
  {
    id: "support.concierge_handoff",
    title: "Concierge Handoff",
    description:
      "Concierge-to-support handoff mode, customer-facing copy, external forwarding, and email collection.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: {
      kind: "teamSettings",
      path: "settings.concierge.ticketHandoffSettings",
      product: "support",
    },
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    ...requiresSupportEnabled,
    fullPageTarget: { type: "settings", path: "customer-widget?tab=support" },
    synonyms: [
      "AI handoff",
      "human handoff",
      "ticket handoff",
      "email handoff",
      "handoff message",
    ],
    dependencies: [
      {
        group: "support.live_chat_widget",
        relationship: "related",
        description:
          "Handoff copy and routing often appears through the customer widget.",
      },
    ],
  },
] as const satisfies readonly SettingGroupDefinition<SupportFlatTeamSettingGroupId>[];

// Compile-time guard: if you add a new id to
// `SupportFlatTeamSettingGroupId` but forget to add the matching entry
// to `SupportFlatTeamSettingGroups`, this `extends` check fails. The
// cast on the actual export is only bridging Object.fromEntries'
// loose `Record<string, V>` return type to the known-narrow keyset —
// this guard makes that bridge safe.
type _assertAllSupportFlatTeamSettingGroupIdsProvided =
  SupportFlatTeamSettingGroupId extends (typeof SupportFlatTeamSettingGroups)[number]["id"]
    ? true
    : never;
const _allSupportFlatTeamSettingGroupIdsProvided: _assertAllSupportFlatTeamSettingGroupIdsProvided = true;
void _allSupportFlatTeamSettingGroupIdsProvided;

export const SupportFlatTeamSettingGroupById: Record<
  SupportFlatTeamSettingGroupId,
  SettingGroupDefinition<SupportFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      SupportFlatTeamSettingGroupId,
      SettingGroupDefinition<SupportFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of SupportFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    SupportFlatTeamSettingGroupId,
    SettingGroupDefinition<SupportFlatTeamSettingGroupId>
  >;
})();
